"use client";

import React, { useState, useRef } from "react";
import Image from "next/image";
import { VideoCardProps } from "../types";
import PlayIcon from "../../../../../public/images/New-landing/play-icon.svg";

const VideoCard: React.FC<VideoCardProps> = ({
  thumbnail,
  videoSrc,
  youtubeUrl,
  onPlayClick,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const getYouTubeEmbedUrl = (url: string) => {
    const videoId = url.split('v=')[1]?.split('&')[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : '';
  };

  const handlePlayClick = () => {
    if (youtubeUrl) {
      setIsPlaying(true);
      onPlayClick?.();
    } else if (videoSrc && videoRef.current) {
      setIsPlaying(true);
      videoRef.current.play();
      onPlayClick?.();
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handlePlayClick();
    }
  };

  return (
    <div className="video-card-container">
      <div className="video-card-border">
        <div className="video-card-content relative">
          <Image
            src={thumbnail}
            alt="Video Thumbnail"
            width={400}
            height={225}
            className={`w-full h-auto ${isPlaying ? "hidden" : "block"}`}
          />

          {youtubeUrl && isPlaying ? (
            <iframe
              src={getYouTubeEmbedUrl(youtubeUrl)}
              className="w-full h-[287px] aspect-video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="YouTube video"
            />
          ) : (
            <video
              ref={videoRef}
              className={`w-full h-auto ${isPlaying ? "block" : "hidden"}`}
              controls
              loop
              onEnded={handleVideoEnd}
              onError={() => setIsPlaying(false)}
            >
              <source src={videoSrc} type="video/mp4" />
              <track kind="captions" src="" label="No captions available" />
              Your browser does not support the video tag.
            </video>
          )}

          {!isPlaying && (
            <div
              className="absolute inset-0 flex items-center justify-center cursor-pointer group"
              onClick={handlePlayClick}
              onKeyDown={handleKeyDown}
              role="button"
              tabIndex={0}
              aria-label="Play video"
            >
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <PlayIcon />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
